import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { cart, update, remove } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [shipping, setShipping] = useState({ name: "", line1: "", city: "", postal_code: "", country: "US" });
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(null);

  if (!user) {
    return <div data-testid="cart-auth-needed" className="pt-32 pb-20 text-center text-white/70">Please <Link to="/login" className="text-tv-accent">sign in</Link> to view your cart.</div>;
  }

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.get(`/coupons/validate/${coupon.toUpperCase()}?subtotal=${cart.subtotal}`);
      setDiscount(data.discount_usd);
      setCouponApplied(data.code);
      toast.success(`Applied ${data.code} — save $${data.discount_usd.toFixed(2)}`);
    } catch (e) {
      setDiscount(0);
      setCouponApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  const checkout = async (e) => {
    e.preventDefault();
    if (!cart.items.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        shipping,
        origin_url: window.location.origin,
        coupon_code: couponApplied || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="cart-page" className="pt-32 pb-20 px-4 sm:px-8 md:px-12 max-w-5xl mx-auto">
      <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tighter flex items-center gap-3">
        <ShoppingBag className="w-8 h-8 text-tv-accent" /> Your cart
      </h1>

      {cart.items.length === 0 ? (
        <div data-testid="cart-empty" className="mt-10 text-center text-white/60">
          Your cart is empty. <Link to="/shop" className="text-tv-accent">Start shopping →</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          <div className="md:col-span-2 space-y-3" data-testid="cart-items">
            {cart.items.map((it) => (
              <div key={it.product_id} data-testid={`cart-line-${it.product_id}`} className="tv-glass rounded-xl p-3 flex items-center gap-3">
                <img src={it.image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=160&q=80"} alt="" className="w-20 h-20 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">{it.title}</div>
                  <div className="text-xs text-white/50">{it.creator_name}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => update(it.product_id, Math.max(0, it.qty - 1))} className="w-7 h-7 rounded-full bg-white/10 text-white">-</button>
                    <span className="text-white font-semibold w-6 text-center">{it.qty}</span>
                    <button onClick={() => update(it.product_id, it.qty + 1)} className="w-7 h-7 rounded-full bg-white/10 text-white">+</button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-tv-accent font-black">${it.line_total.toFixed(2)}</div>
                  <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-red-400 mt-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={checkout} className="tv-glass rounded-2xl p-5 h-fit space-y-3" data-testid="checkout-form">
            <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">Order summary</div>
            <div className="flex justify-between text-white text-sm"><span>Items ({cart.count})</span><span className="font-bold">${cart.subtotal.toFixed(2)}</span></div>
            <div className="flex gap-1">
              <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm uppercase" />
              <button data-testid="coupon-apply-btn" type="button" onClick={applyCoupon} className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold">Apply</button>
            </div>
            {couponApplied && (
              <div data-testid="coupon-applied" className="flex justify-between text-green-300 text-sm">
                <span>{couponApplied} discount</span><span className="font-bold">−${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-white/60 text-xs"><span>Shipping</span><span>Calculated at checkout</span></div>
            <div className="border-t border-white/10 pt-3 flex justify-between items-end">
              <span className="text-white/70 text-sm">Total</span>
              <span className="text-tv-accent font-display font-black text-2xl">${Math.max(0.5, cart.subtotal - discount).toFixed(2)}</span>
            </div>

            <div className="pt-3 border-t border-white/10 space-y-2">
              <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">Shipping address</div>
              <input data-testid="ship-name" placeholder="Full name" required value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
              <input data-testid="ship-line1" placeholder="Address line" required value={shipping.line1} onChange={(e) => setShipping({ ...shipping, line1: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="ship-city" placeholder="City" required value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <input data-testid="ship-zip" placeholder="Postal code" required value={shipping.postal_code} onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
              </div>
              <input data-testid="ship-country" placeholder="Country (ISO2)" required value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            </div>

            <button data-testid="checkout-btn" type="submit" disabled={busy} className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-bold py-3 rounded-full disabled:opacity-60 flex items-center justify-center gap-2">
              {busy ? "Redirecting…" : (<>Checkout <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
